import { useEffect, useRef, useState } from "react";

// Genuine user-input events. Deliberately NOT ``visibilitychange``:
// returning to a tab that sat idle overnight is not activity, and counting
// it would reset the timer at the exact moment it should be firing.
const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
] as const;

// mousemove/scroll fire in bursts; only re-arm the timer at most this
// often so an active user does not thrash setTimeout on every pixel.
const THROTTLE_MS = 1000;

interface IdleOptions {
  /** Total idle time before logout. */
  timeoutMs: number;
  /** How long BEFORE the timeout to raise the warning. The countdown the
   *  dialog shows is this many milliseconds. */
  warnMs: number;
  onIdle: () => void;
}

interface IdleState {
  /** True while the "still there?" warning should be shown. */
  warning: boolean;
  /** Whole seconds left before logout, for the countdown. 0 when idle. */
  secondsLeft: number;
  /** "Stay signed in" — re-arm the full timer and clear the warning. */
  stayActive: () => void;
}

/**
 * Idle auto-logoff with a warning phase — HIPAA-style automatic logoff for
 * a PHI workstation, but announced rather than silent.
 *
 * For the first ``timeoutMs - warnMs`` any real input resets the clock
 * with no interruption. If that lapses, the warning fires and a countdown
 * of ``warnMs`` begins; during it, background activity is deliberately
 * IGNORED so the prompt is a genuine checkpoint — the user resolves it
 * with the "Stay signed in" button (``stayActive``) or is logged out at
 * zero. A pending timer that comes due while the machine sleeps fires on
 * wake, so a session left open overnight is gone by morning.
 */
export function useIdleTimeout(
  enabled: boolean,
  { timeoutMs, warnMs, onIdle }: IdleOptions,
): IdleState {
  const onIdleRef = useRef(onIdle);
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  // ``resetRef`` lets the returned ``stayActive`` button and the event
  // listeners share the one re-arm implementation created inside the effect.
  const resetRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!enabled) {
      setSecondsLeft(null);
      return;
    }

    let warnTimer: ReturnType<typeof setTimeout> | null = null;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    let countdown: ReturnType<typeof setInterval> | null = null;
    let warningShown = false;

    const clearAll = () => {
      if (warnTimer !== null) clearTimeout(warnTimer);
      if (idleTimer !== null) clearTimeout(idleTimer);
      if (countdown !== null) clearInterval(countdown);
      warnTimer = idleTimer = countdown = null;
    };

    const startWarning = () => {
      warningShown = true;
      const deadline = Date.now() + warnMs;
      setSecondsLeft(Math.ceil(warnMs / 1000));
      countdown = setInterval(() => {
        setSecondsLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
      }, 500);
    };

    const arm = () => {
      clearAll();
      warningShown = false;
      setSecondsLeft(null);
      warnTimer = setTimeout(startWarning, Math.max(0, timeoutMs - warnMs));
      idleTimer = setTimeout(() => {
        clearAll();
        setSecondsLeft(null);
        onIdleRef.current();
      }, timeoutMs);
    };
    resetRef.current = arm;

    let lastReset = 0;
    const onActivity = () => {
      // Once the warning is up it must be answered with the button — a
      // stray mousemove should not silently keep a walked-away session
      // alive. Before the warning, any activity keeps the session fresh.
      if (warningShown) return;
      const now = Date.now();
      if (now - lastReset < THROTTLE_MS) return;
      lastReset = now;
      arm();
    };

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }
    arm();

    return () => {
      clearAll();
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity);
      }
    };
  }, [enabled, timeoutMs, warnMs]);

  return {
    warning: secondsLeft !== null,
    secondsLeft: secondsLeft ?? 0,
    stayActive: () => resetRef.current(),
  };
}
